// Alertas por e-mail da estação: LIMIARES por variável + QUEM RECEBE.
//
// Mora na aba Configuração porque é configuração do equipamento, não análise de
// dado — quem mapeia o canal é quem sabe o que é valor normal para ele.
//
// Duas colunas existem aqui que costumam faltar em tela de alerta, e são elas
// que decidem se o alerta é lido ou vira ruído:
//   • "leituras seguidas" — uma decodificação ruim não pode acordar ninguém às
//     3h. Não é hipótese: o NivelBat tem 64 leituras acima de 30 V vindas de um
//     decoder de 2 bytes recebendo lixo.
//   • "histerese" — sem ela, valor oscilando em cima do limite manda e-mail a
//     cada ciclo, e o destinatário cria regra de caixa de entrada.
import { useMemo, useState } from 'react';
import {
  App as AntApp, Button, Input, InputNumber, Select, Space, Table, Tag, Alert,
  Typography, Popconfirm, Tooltip, Switch, Card, Checkbox,
} from 'antd';
import { SaveOutlined, PlusOutlined, DeleteOutlined, BulbOutlined, MailOutlined } from '@ant-design/icons';
import { api } from './shared';
import { Variavel } from './ModalVariavelNova';

const { Text } = Typography;

export type Limiar = {
  id?: string; variavel: string; severidade: 'aviso' | 'critico';
  direcao: 'abaixo' | 'acima'; valor: number | null;
  min_amostras: number | null; histerese: number | null; ativo: boolean;
  avisar_dias_antes?: number | null;
  observacao?: string | null; rotulo?: string | null; unidade?: string | null;
  em_alerta?: boolean | null; em_alerta_desde?: string | null;
  _novo?: boolean;
};
export type Destinatario = {
  id?: string; email: string; tipo: string; ativo: boolean; curinga?: boolean; _novo?: boolean;
};
export type LimiarSugerido = {
  codigo: string; rotulo: string; variavel_sugerida: string | null;
  limiar_aviso_min: number | null; limiar_critico_min: number | null;
  limiar_aviso_max: number | null; limiar_histerese: number | null;
  limiar_justificativa: string | null;
};

const fmt = (v: number | null | undefined) =>
  v == null ? '—' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 3 });

export function SecaoAlertas({ estacaoId, variaveis, limiares, destinatarios, sugeridos, podeConfigurar, onSalvo }: {
  estacaoId: string;
  variaveis: Variavel[];
  limiares: Limiar[];
  destinatarios: Destinatario[];
  sugeridos: LimiarSugerido[];
  podeConfigurar: boolean;
  onSalvo: () => void;
}) {
  const { message } = AntApp.useApp();
  const [lim, setLim] = useState<Limiar[]>(limiares);
  const [dest, setDest] = useState<Destinatario[]>(destinatarios);
  const [salvando, setSalvando] = useState(false);
  const [novoEmail, setNovoEmail] = useState('');
  const [novoTipos, setNovoTipos] = useState<string[]>(['limiar', 'silencio']);

  const setL = (i: number, patch: Partial<Limiar>) =>
    setLim(ls => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const varPorCodigo = useMemo(() => {
    const m = new Map<string, Variavel>();
    variaveis.forEach(v => m.set(v.codigo, v));
    return m;
  }, [variaveis]);

  // Só as variáveis que a estação realmente tem limiar de sentido: qualquer uma
  // do catálogo com faixa. Sem faixa (posição, estado) não há "acima/abaixo".
  const opcoesVar = useMemo(() => variaveis
    .filter(v => v.faixa_min != null)
    .map(v => ({ value: v.codigo, label: `${v.codigo}${v.unidade ? ` (${v.unidade})` : ''}` })),
    [variaveis]);

  async function salvar(extra: any = {}) {
    setSalvando(true);
    try {
      const r = await api('op_estacao_alertas_salvar', {
        id: estacaoId, limiares: lim, destinatarios: dest, ...extra,
      });
      if (r?.aplicados > 0) message.success(`${r.aplicados} limiar(es) sugerido(s) aplicado(s).`);
      else message.success('Alertas salvos.');
      setLim(r.limiares || []);
      setDest(r.destinatarios || []);
      onSalvo();
    } catch (e: any) {
      message.error(e.response?.data?.error || e.message);
    } finally { setSalvando(false); }
  }

  const colunas = [
    {
      title: 'Variável', dataIndex: 'variavel', width: 180,
      render: (v: string, r: Limiar, i: number) => (
        <Space direction="vertical" size={0} style={{ width: '100%' }}>
          <Select size="small" style={{ width: '100%' }} value={v || undefined} showSearch
            optionFilterProp="label" disabled={!podeConfigurar} options={opcoesVar}
            placeholder="escolha" onChange={(val) => setL(i, { variavel: String(val || '') })} />
          {varPorCodigo.get(v) && (
            <Text type="secondary" style={{ fontSize: 10 }}>
              faixa {fmt(varPorCodigo.get(v)!.faixa_min)} a {fmt(varPorCodigo.get(v)!.faixa_max)} {varPorCodigo.get(v)!.unidade}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Quando', width: 230,
      render: (_: any, r: Limiar, i: number) => (
        <Space size={4}>
          <Select size="small" style={{ width: 92 }} value={r.direcao} disabled={!podeConfigurar}
            options={[{ value: 'abaixo', label: 'abaixo de' }, { value: 'acima', label: 'acima de' }]}
            onChange={(val) => setL(i, { direcao: val as Limiar['direcao'] })} />
          <InputNumber size="small" decimalSeparator="," style={{ width: 100 }} value={r.valor ?? undefined}
            disabled={!podeConfigurar} onChange={(val) => setL(i, { valor: val as number })} />
          <Text type="secondary" style={{ fontSize: 11 }}>{r.unidade || varPorCodigo.get(r.variavel)?.unidade || ''}</Text>
        </Space>
      ),
    },
    {
      title: 'Severidade', dataIndex: 'severidade', width: 110,
      render: (v: string, _: Limiar, i: number) => (
        <Select size="small" style={{ width: '100%' }} value={v} disabled={!podeConfigurar}
          options={[{ value: 'aviso', label: 'Aviso' }, { value: 'critico', label: 'Crítico' }]}
          onChange={(val) => setL(i, { severidade: val as Limiar['severidade'] })} />
      ),
    },
    {
      title: <Tooltip title="Quantas leituras seguidas precisam violar antes de mandar e-mail. Uma decodificação ruim não deve acordar ninguém às 3h.">Leituras seguidas</Tooltip>,
      width: 110,
      render: (_: any, r: Limiar, i: number) => (
        <InputNumber size="small" style={{ width: '100%' }} min={1} max={100} value={r.min_amostras ?? 3}
          disabled={!podeConfigurar} onChange={(val) => setL(i, { min_amostras: val as number })} />
      ),
    },
    {
      title: <Tooltip title="Quanto precisa recuperar para sair do alerta. Sem isso, valor oscilando em cima do limite manda e-mail a cada ciclo.">Histerese</Tooltip>,
      width: 110,
      render: (_: any, r: Limiar, i: number) => (
        <InputNumber size="small" decimalSeparator="," style={{ width: '100%' }} min={0}
          value={r.histerese ?? 0} disabled={!podeConfigurar}
          onChange={(val) => setL(i, { histerese: val as number })} />
      ),
    },
    {
      title: <Tooltip title="Avisa ANTES de cruzar, pela tendência dos últimos 7 dias: 'no ritmo atual chega em N dias'. Serve para o que deriva devagar (bateria); deixe vazio para nível de rio, porque cheia não se projeta por reta.">Avisar dias antes</Tooltip>,
      width: 110,
      render: (_: any, r: Limiar, i: number) => (
        <InputNumber size="small" style={{ width: '100%' }} min={1} max={365}
          value={r.avisar_dias_antes ?? undefined} placeholder="não" disabled={!podeConfigurar}
          onChange={(val) => setL(i, { avisar_dias_antes: val as number })} />
      ),
    },
    {
      title: 'Ativo', dataIndex: 'ativo', width: 64, align: 'center' as const,
      render: (v: boolean, _: Limiar, i: number) => (
        <Switch size="small" checked={v !== false} disabled={!podeConfigurar}
          onChange={(c) => setL(i, { ativo: c })} />
      ),
    },
    {
      title: 'Estado', width: 130,
      render: (_: any, r: Limiar) => {
        if (r._novo) return <Text type="secondary" style={{ fontSize: 11 }}>falta salvar</Text>;
        if (r.ativo === false) return <Tag>desligado</Tag>;
        if (r.em_alerta) return <Tag color="red">EM ALERTA</Tag>;
        return <Tag color="green">normal</Tag>;
      },
    },
    ...(podeConfigurar ? [{
      title: '', width: 44,
      render: (_: any, r: Limiar, i: number) => (
        <Popconfirm title="Remover este limiar?"
          onConfirm={() => {
            if (r.id) { setLim(ls => ls.filter((_x, j) => j !== i)); salvarExclusao(r); }
            else setLim(ls => ls.filter((_x, j) => j !== i));
          }}>
          <Button size="small" danger type="text" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    }] : []),
  ];

  async function salvarExclusao(r: Limiar) {
    setSalvando(true);
    try {
      await api('op_estacao_alertas_salvar', { id: estacaoId, limiares: [{ id: r.id, excluir: true }] });
      message.success('Limiar removido.');
      onSalvo();
    } catch (e: any) { message.error(e.response?.data?.error || e.message); }
    finally { setSalvando(false); }
  }

  async function removerDestinatario(d: Destinatario) {
    if (!d.id) { setDest(ds => ds.filter(x => x !== d)); return; }
    setSalvando(true);
    try {
      await api('op_estacao_alertas_salvar', { id: estacaoId, destinatarios: [{ id: d.id, excluir: true }] });
      message.success('Destinatário removido.');
      onSalvo();
    } catch (e: any) { message.error(e.response?.data?.error || e.message); }
    finally { setSalvando(false); }
  }

  const temSugestao = sugeridos.length > 0;
  const jaAplicou = temSugestao && sugeridos.every(s =>
    lim.some(l => l.variavel === s.variavel_sugerida));

  return (
    <Card size="small" title={<Space><MailOutlined />Alertas por e-mail</Space>}
      style={{ marginTop: 12 }} styles={{ body: { padding: 12 } }}>

      {temSugestao && !jaAplicou && podeConfigurar && (
        <Alert type="info" showIcon icon={<BulbOutlined />} style={{ marginBottom: 12 }}
          message={`Há limiares sugeridos para o equipamento desta estação (${sugeridos.map(s => s.rotulo).join(', ')})`}
          description={
            <Space direction="vertical" size={2}>
              {sugeridos.map(s => (
                <Text key={s.codigo} type="secondary" style={{ fontSize: 11 }}>
                  <b>{s.variavel_sugerida}</b>
                  {s.limiar_aviso_min != null && ` · aviso abaixo de ${fmt(s.limiar_aviso_min)}`}
                  {s.limiar_critico_min != null && ` · crítico abaixo de ${fmt(s.limiar_critico_min)}`}
                  {s.limiar_aviso_max != null && ` · aviso acima de ${fmt(s.limiar_aviso_max)}`}
                  {s.limiar_justificativa && <><br />{s.limiar_justificativa}</>}
                </Text>
              ))}
            </Space>}
          action={<Button size="small" type="primary" loading={salvando}
            onClick={() => salvar({ aplicar_sugestao: true })}>Aplicar</Button>} />
      )}

      <Table size="small" rowKey={(r: any) => r.id || `${r.variavel}-${r.severidade}-${r.direcao}`}
        dataSource={lim} columns={colunas as any} pagination={false} scroll={{ x: 1090 }}
        locale={{ emptyText: 'Nenhum limiar. Sem limiar, ninguém é avisado quando o valor sai do normal.' }} />

      {podeConfigurar && (
        <Space style={{ marginTop: 10 }} wrap>
          <Button size="small" icon={<PlusOutlined />} onClick={() => setLim(ls => [...ls, {
            variavel: '', severidade: 'aviso', direcao: 'abaixo', valor: null,
            min_amostras: 3, histerese: 0, ativo: true, avisar_dias_antes: null, _novo: true,
          }])}>Adicionar limiar</Button>
          <Button size="small" type="primary" icon={<SaveOutlined />} loading={salvando}
            onClick={() => salvar()}>Salvar alertas</Button>
        </Space>
      )}

      {/* Quem recebe. O curinga (todas as estações) aparece junto e NÃO é
          editável aqui — quem abriu UMA estação não deve, sem perceber, tirar
          alguém da lista global. */}
      <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
        <Text strong style={{ fontSize: 13 }}>Quem recebe os alertas desta estação</Text>
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {dest.length === 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Ninguém cadastrado — os alertas seriam calculados e não avisariam ninguém.
            </Text>
          )}
          {dest.map((d, i) => (
            <Tag key={d.id || `n${i}`} color={d.curinga ? 'default' : 'blue'}
              closable={podeConfigurar && !d.curinga}
              onClose={(e) => { e.preventDefault(); removerDestinatario(d); }}>
              {d.email} · {d.tipo === 'silencio' ? 'silêncio' : 'limiar'}
              {d.curinga && ' · todas as estações'}
            </Tag>
          ))}
        </div>
        {podeConfigurar && (
          <Space style={{ marginTop: 10 }} wrap align="start">
            <Input size="small" style={{ width: 260 }} placeholder="pessoa@empresa.com.br"
              value={novoEmail} onChange={e => setNovoEmail(e.target.value)} />
            <Checkbox.Group value={novoTipos} onChange={(v) => setNovoTipos(v as string[])}
              options={[{ label: 'limiar', value: 'limiar' }, { label: 'silêncio', value: 'silencio' }]} />
            <Button size="small" loading={salvando} disabled={!novoEmail.trim() || !novoTipos.length}
              onClick={async () => {
                await salvar({ destinatarios: [...dest, { email: novoEmail.trim(), tipos: novoTipos, ativo: true }] });
                setNovoEmail('');
              }}>Adicionar</Button>
          </Space>
        )}
      </div>
    </Card>
  );
}
